import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Bot, User, AlertCircle, CheckCircle, RefreshCw, Send, X, BarChart3, ThumbsUp, ThumbsDown } from 'lucide-react';
import { theme, cardStyle, inputStyle } from '../theme';

export default function FrontDeskInboxPage({ API_BASE }) {
  const [tab, setTab] = useState('inbox'); // inbox | analytics
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter]   = useState('');
  const [reply, setReply]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [insights, setInsights]   = useState(null);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    const params = filter ? { status: filter } : {};
    const r = await axios.get(`${API_BASE}/frontdesk/threads`, { params });
    setThreads(r.data || []);
    if (selected) {
      const fresh = r.data.find(t => t._id === selected._id);
      if (fresh) setSelected(fresh);
    }
  }, [API_BASE, filter, selected]);

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [selected]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/frontdesk/threads/${selected._id}/reply`, { text: reply.trim() });
      setSelected(r.data); setReply(''); load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  const close = async () => {
    if (!selected) return;
    await axios.post(`${API_BASE}/frontdesk/threads/${selected._id}/close`);
    load();
  };

  // Rate an individual AI message (RLHF-lite for continuous learning).
  const rateMessage = async (msgId, rating) => {
    if (!selected) return;
    let note = '';
    if (rating === 'bad') {
      note = window.prompt('Why was this reply bad? (optional)') || '';
    }
    try {
      await axios.post(`${API_BASE}/frontdesk/threads/${selected._id}/messages/${msgId}/rate`, { rating, note });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const loadAnalytics = useCallback(async () => {
    try {
      const [a, i] = await Promise.all([
        axios.get(`${API_BASE}/frontdesk/analytics?days=30`),
        axios.get(`${API_BASE}/frontdesk/insights?days=30`),
      ]);
      setAnalytics(a.data); setInsights(i.data);
    } catch (e) { /* silent */ }
  }, [API_BASE]);

  useEffect(() => { if (tab === 'analytics') loadAnalytics(); }, [tab, loadAnalytics]);

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14, alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0, color: 'var(--color-text)' }}>🤖 AI Front Desk</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { key: 'inbox',     label: 'Inbox',     icon: MessageSquare },
            { key: 'analytics', label: 'Analytics', icon: BarChart3 },
          ].map(t => {
            const Ic = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: active ? 'var(--color-accent)' : 'var(--color-bg-inset)',
                  color: active ? 'white' : 'var(--color-text)',
                }}>
                <Ic size={14}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'inbox' && (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div />
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={filter} onChange={e=>setFilter(e.target.value)} style={{ ...inputStyle, width: 160 }}>
            <option value="">All threads</option>
            <option value="ai">AI handling</option>
            <option value="escalated">Escalated</option>
            <option value="closed">Closed</option>
          </select>
          <button onClick={()=>setShowTest(true)} style={btn(theme.secondary)}><Bot size={14}/> Test AI</button>
          <button onClick={load} style={btn(theme.primary)}><RefreshCw size={14}/> Refresh</button>
        </div>
      </div>
      )}

      {tab === 'analytics' && <AnalyticsPanel analytics={analytics} insights={insights} onRefresh={loadAnalytics} />}

      {tab === 'inbox' && (
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 14, height: 'calc(100vh - 180px)' }}>
        <div style={{ ...cardStyle, padding: 0, overflowY: 'auto' }}>
          {threads.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
              <MessageSquare size={32} style={{ opacity: 0.4 }}/>
              <div style={{ marginTop: 8, fontSize: 13 }}>Koi conversation nahi hai. Patient WhatsApp pe message bhejega to yahan dikhega.</div>
            </div>
          ) : threads.map(t => (
            <div key={t._id} onClick={()=>setSelected(t)} style={{
              padding: 12, borderBottom: `1px solid ${theme.borderLight}`, cursor: 'pointer',
              background: selected?._id === t._id ? theme.primaryLight : 'transparent'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 14 }}>{t.patientName || t.phone}</strong>
                {t.status === 'escalated' && <span style={pill(theme.danger)}><AlertCircle size={11}/> Esc</span>}
                {t.status === 'closed' && <span style={pill(theme.textMuted)}><CheckCircle size={11}/> Closed</span>}
              </div>
              <div style={{ fontSize: 11.5, color: theme.textMuted, marginTop: 3 }}>
                {t.intent && <span style={{ marginRight: 6 }}>· {t.intent}</span>}
                {t.messages?.length || 0} msgs
              </div>
              {t.messages?.length > 0 && (
                <div style={{ fontSize: 12, color: theme.navy, marginTop: 4, opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.messages[t.messages.length-1].text}
                </div>
              )}
              <div style={{ fontSize: 10, color: theme.textSubtle, marginTop: 4 }}>
                {new Date(t.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ ...cardStyle, padding: 0, display: 'flex', flexDirection: 'column' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted }}>
              👈 Select a conversation to view
            </div>
          ) : (
            <>
              <div style={{ padding: 14, borderBottom: `1px solid ${theme.borderLight}`, display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <strong style={{ color: theme.navy }}>{selected.patientName || selected.phone}</strong>
                  <div style={{ fontSize: 12, color: theme.textMuted }}>📱 {selected.phone} · {selected.channel}</div>
                </div>
                {selected.status !== 'closed' && <button onClick={close} style={btn(theme.textMuted)}>Close thread</button>}
              </div>

              <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--color-bg-inset)' }}>
                {selected.messages?.map((m, i) => {
                  const isPatient = m.role === 'patient';
                  const isAi = m.role === 'ai';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: isPatient ? 'flex-start' : 'flex-end', marginBottom: 8 }}>
                      <div style={{
                        maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
                        background: 'var(--color-bg-card)',
                        border: `1px solid ${isAi ? 'var(--color-accent-border)' : 'var(--color-border)'}`,
                        color: 'var(--color-text)',
                      }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, marginBottom: 4, color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>
                          {isPatient ? <><User size={10}/> Patient</> : isAi ? <><Bot size={10}/> AI</> : 'Staff'}
                          {/* RLHF rating shown if set */}
                          {isAi && m.rating === 'good' && <span style={{ marginLeft: 6, color: theme.success }}>👍 good</span>}
                          {isAi && m.rating === 'bad' && <span style={{ marginLeft: 6, color: theme.danger }}>👎 bad</span>}
                        </div>
                        <div style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.text}</div>
                        <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span>{new Date(m.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                          {/* RLHF rating buttons — only on AI messages */}
                          {isAi && m._id && (
                            <span style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => rateMessage(m._id, m.rating === 'good' ? 'none' : 'good')}
                                title="Mark this AI reply as good"
                                style={{ padding: '2px 6px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                  background: m.rating === 'good' ? theme.success : 'transparent',
                                  color: m.rating === 'good' ? 'white' : 'var(--color-text-tertiary)',
                                }}>
                                <ThumbsUp size={11}/>
                              </button>
                              <button onClick={() => rateMessage(m._id, m.rating === 'bad' ? 'none' : 'bad')}
                                title="Mark this AI reply as bad (helps the system learn)"
                                style={{ padding: '2px 6px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                  background: m.rating === 'bad' ? theme.danger : 'transparent',
                                  color: m.rating === 'bad' ? 'white' : 'var(--color-text-tertiary)',
                                }}>
                                <ThumbsDown size={11}/>
                              </button>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: 12, borderTop: `1px solid ${theme.borderLight}`, display: 'flex', gap: 8 }}>
                <input value={reply} onChange={e=>setReply(e.target.value)} onKeyDown={e=>{ if (e.key === 'Enter') sendReply(); }} placeholder="Type your reply..." style={{ ...inputStyle, flex: 1, marginBottom: 0 }}/>
                <button onClick={sendReply} disabled={busy || !reply.trim()} style={btn(theme.primary)}><Send size={14}/> {busy ? '...' : 'Send'}</button>
              </div>
            </>
          )}
        </div>
      </div>

      )}

      {showTest && <TestAiModal API_BASE={API_BASE} onClose={()=>setShowTest(false)} onSent={()=>{ setShowTest(false); load(); }}/>}
    </div>
  );
}

// ── Analytics dashboard panel ──
// Surfaces volume / peak hour / intent breakdown / sentiment / conversion
// + the continuous-learning insights (worst-rated AI replies grouped by intent).
function AnalyticsPanel({ analytics, insights, onRefresh }) {
  if (!analytics) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading insights…</div>;
  }
  const a = analytics;
  const fmtPct = (n) => (n == null ? '—' : `${n}%`);
  const fmtSec = (n) => (n == null ? '—' : `${n}s`);

  const Stat = ({ label, value, hint, color = 'var(--color-text)' }) => (
    <div style={{ ...cardStyle, padding: 14 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{hint}</div>}
    </div>
  );

  // Bar for hourly heatmap
  const maxHour = Math.max(...a.byHour, 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Last <strong>{a.windowDays}</strong> days · {a.total} conversations</div>
        <button onClick={onRefresh} style={btn(theme.primary)}><RefreshCw size={13}/> Refresh</button>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <Stat label="Total Threads" value={a.total} />
        <Stat label="Booking Rate" value={fmtPct(a.bookingRate)} color={theme.success} hint="conversations that ended in appointment" />
        <Stat label="Auto-Resolve Rate" value={fmtPct(a.autoResolveRate)} color={theme.info} hint="AI handled fully — no staff needed" />
        <Stat label="Escalation Rate" value={fmtPct(a.escalationRate)} color={theme.warning} hint="needed human takeover" />
        <Stat label="Avg First Response" value={fmtSec(a.avgFirstResponseSec)} hint="seconds patient waited" />
        <Stat label="Peak Hour" value={`${a.peakHour}:00`} hint="busiest time of day" />
      </div>

      {/* Hourly heatmap */}
      <div style={{ ...cardStyle, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>Volume by Hour (0–23)</div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 120 }}>
          {a.byHour.map((c, h) => (
            <div key={h} title={`${h}:00 — ${c} threads`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div style={{
                width: '100%',
                height: `${(c / maxHour) * 100}%`,
                background: h === a.peakHour ? theme.warning : 'var(--color-accent)',
                borderRadius: '3px 3px 0 0', minHeight: c > 0 ? 2 : 0,
                transition: 'height 0.3s',
              }}/>
              <div style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}>{h}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Intent + Sentiment side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={{ ...cardStyle, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>Intents</div>
          {Object.keys(a.intents).length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>No data yet</div>}
          {Object.entries(a.intents).sort((x, y) => y[1] - x[1]).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: 'var(--color-text)' }}>
              <span>{k}</span>
              <strong>{v}</strong>
            </div>
          ))}
        </div>
        <div style={{ ...cardStyle, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>Sentiment</div>
          {Object.entries(a.sentiments).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: 'var(--color-text)' }}>
              <span style={{ textTransform: 'capitalize' }}>{k}</span>
              <strong style={{ color: k === 'positive' ? theme.success : k === 'negative' ? theme.danger : 'var(--color-text-secondary)' }}>{v}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* Continuous-learning panel — worst intents grouped */}
      <div style={{ ...cardStyle, padding: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 10, textTransform: 'uppercase' }}>
          🧠 Continuous Learning — Intents Needing Attention
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
          AI replies that staff marked 👎 bad. Use these examples to refine prompts and templates.
        </div>
        {!insights || insights.badIntents.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', padding: 8 }}>
            No bad-rated AI replies in the last 30 days. 🎉
          </div>
        ) : (
          insights.badIntents.map(({ intent, count, examples }) => (
            <div key={intent} style={{ marginBottom: 14, padding: 10, borderRadius: 8, background: 'var(--color-bg-inset)', borderLeft: `3px solid ${theme.danger}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
                {intent} <span style={{ color: theme.danger, fontSize: 11, marginLeft: 6 }}>{count} flagged</span>
              </div>
              {examples.map((ex, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '4px 0', borderBottom: i < examples.length - 1 ? '1px dashed var(--color-border)' : 'none' }}>
                  <em>"{ex.text.slice(0, 120)}{ex.text.length > 120 ? '…' : ''}"</em>
                  {ex.note && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Reviewer note: {ex.note}</div>}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function TestAiModal({ API_BASE, onClose, onSent }) {
  const [phone, setPhone] = useState('+919999999999');
  const [text, setText]   = useState('Hi, mujhe kal ek check-up book karna hai. Slot available hai?');
  const [busy, setBusy]   = useState(false);
  const [reply, setReply] = useState(null);

  const send = async () => {
    setBusy(true); setReply(null);
    try {
      const r = await axios.post(`${API_BASE}/frontdesk/inbound`, { phone, text, dryRun: true });
      const ai = (r.data.messages || []).filter(m => m.role === 'ai').slice(-1)[0];
      setReply(ai?.text || '(no reply)');
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(560px, 92vw)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>🧪 Test AI Front Desk (Dry Run)</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 14 }}>
          <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Patient phone" style={inputStyle}/>
          <textarea rows={3} value={text} onChange={e=>setText(e.target.value)} placeholder="Patient ka message" style={{ ...inputStyle, resize: 'vertical' }}/>
          <button onClick={send} disabled={busy} style={{ ...btn(theme.primary), width: '100%' }}>{busy ? 'AI thinking...' : '🤖 See AI reply'}</button>
        </div>
        {reply && (
          <div style={{ marginTop: 14, padding: 12, background: theme.secondaryLight, borderRadius: theme.radiusSm }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.secondaryDark, marginBottom: 6 }}>🤖 AI WOULD REPLY:</div>
            <div style={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{reply}</div>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>(Dry run — no message sent)</div>
          </div>
        )}
      </div>
    </div>
  );
}

const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const pill = (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: c + '20', color: c, borderRadius: 12, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 });
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
